import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './appeals.css';
import type { AppealSent } from './types';
import {
  confirmResend,
  decideSent,
  decideSofia,
  deniedTotalSync,
  getClaimSync,
  getDeniedSync,
  getSentSync,
  getUnderpaidSync,
  haveElvaCall,
  isSentDecided,
  needsYouCountSync,
  resolveClaim,
  sendAppeal,
  sentOpenCountSync,
  touchOpen,
  underpaidShortSync,
  useAppealsVersion,
} from './store';
import { Header, type View } from './components/Header';
import { Queue } from './components/Queue';
import { DeniedDetail } from './components/DeniedDetail';
import { UnderpaidDetail } from './components/UnderpaidDetail';
import { AppealsSent } from './components/AppealsSent';
import { ClosedHistory } from './components/ClosedHistory';
import { CLOSED_HISTORY, DENIED_RESOLVED, closedFromLiveSent } from './historyMock';
import { DecideDialog } from './components/dialogs';
import { NotFoundInPage } from '@/components/NotFoundInPage';
import type { ActionHandlers } from './components/actions';

interface Toast {
  id: number;
  msg: string;
  undoLabel?: string;
  onUndo?: () => void;
}

type Dialog = { type: 'decide'; row: AppealSent } | null;

function useIsDesktop(): boolean {
  const [d, setD] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true));
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const h = () => { setD(m.matches); };
    m.addEventListener('change', h);
    return () => { m.removeEventListener('change', h); };
  }, []);
  return d;
}

export function Appeals({ view: viewProp }: { view?: View }) {
  useAppealsVersion();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { claimId, appealId } = useParams();
  const [searchParams] = useSearchParams();

  const denied = getDeniedSync();
  const underpaid = getUnderpaidSync();
  const sent = getSentSync();

  const [toasts, setToasts] = useState<Toast[]>([]);
  const [dialog, setDialog] = useState<Dialog>(null);
  const toastSeq = useRef(0);

  const selectedClaim = claimId ? getClaimSync(claimId) : null;
  // Denied › Resolved used to live on the existing /denied route under
  // ?tab=resolved (D148 — no new route then). It still works, but now renders
  // the same merged "Closed" pill as /denied/closed (rcm-18 §... 4 pills).
  const isResolved = !claimId && viewProp === 'denied' && searchParams.get('tab') === 'resolved';
  const view: View = claimId && selectedClaim ? selectedClaim.kind : isResolved ? 'closed' : (viewProp ?? 'denied');
  const listClaims = view === 'underpaid' ? underpaid : denied;

  useEffect(() => {
    if (claimId) touchOpen(claimId);
  }, [claimId]);

  // open first claim on a bare tab (desktop) — but not on the Resolved history
  useEffect(() => {
    if (!claimId && !isResolved && isDesktop && (viewProp === 'denied' || viewProp === 'underpaid')) {
      const list = viewProp === 'underpaid' ? underpaid : denied;
      if (list[0]) void navigate(`/denied/${list[0].id}`, { replace: true });
    }
  }, [claimId, isResolved, isDesktop, viewProp, denied, underpaid, navigate]);

  const addToast = (msg: string, undo?: { label: string; onUndo: () => void }, ms = 8000) => {
    const id = ++toastSeq.current;
    setToasts((t) => [...t, { id, msg, ...(undo ? { undoLabel: undo.label, onUndo: undo.onUndo } : {}) }]);
    setTimeout(() => { setToasts((t) => t.filter((x) => x.id !== id)); }, ms);
  };

  const tabTo = (v: View): string =>
    v === 'closed' ? '/denied/closed' : v === 'appeals' ? '/denied/appeals' : v === 'underpaid' ? '/denied/underpaid' : '/denied';
  const goTo = (id: string) => { void navigate(`/denied/${id}`); };

  const doPreview = () => {
    if (claimId) void navigate(`/denied/${claimId}/packet`);
  };

  // Every action on the detail pane stays on the claim and shows its result card (rcm-30 §2).
  const handlers: ActionHandlers = {
    onSend: () => { if (claimId) sendAppeal(claimId); },
    onPreview: doPreview,
    onResend: () => { if (claimId) confirmResend(claimId); },
    onResolve: (outcome, reason, note) => { if (claimId) resolveClaim(claimId, outcome, reason, note === '' ? undefined : note); },
    onHaveElvaCall: () => { if (claimId) haveElvaCall(claimId); },
  };
  const doDecide = (optionId: string, note: string) => {
    if (claimId) decideSofia(claimId, optionId, note === '' ? undefined : note);
  };

  // Same selector the sidebar badge adds to its ready-to-send count (D204).
  const needsYouCount = needsYouCountSync();

  // Appeals closed via Decide this session — live store rows, folded into the
  // merged Closed pill alongside the ~2,800-row history (rcm-18).
  const liveClosedSent = sent.filter((r) => (r.closed ?? false) || isSentDecided(r)).map(closedFromLiveSent);
  const totalClosedCount = CLOSED_HISTORY.length + liveClosedSent.length;

  const header = (
    <Header
      view={view}
      deniedCount={denied.length}
      deniedTotal={deniedTotalSync()}
      underpaidCount={underpaid.length}
      underpaidShort={underpaidShortSync()}
      appealsCount={sentOpenCountSync()}
      closedCount={totalClosedCount}
      needsYouCount={needsYouCount}
      tabTo={tabTo}
    />
  );

  // ---- Closed (rcm-18): merged Denied/Underpaid resolved history + Appeals
  // sent's closed history. Also reachable via the legacy /denied?tab=resolved. ----
  if (view === 'closed') {
    return (
      <div className="apl apl-page">
        {header}
        <ClosedHistory liveClosed={liveClosedSent} />
        {renderToasts()}
      </div>
    );
  }

  // ---- Appeals sent tab (open list; Closed lives on its own pill now) ----
  if (view === 'appeals') {
    return (
      <div className="apl apl-page">
        {header}
        <AppealsSent rows={sent} openId={appealId ?? null} onDecide={(row) => { setDialog({ type: 'decide', row }); }} />
        {renderDialogs()}
        {renderToasts()}
      </div>
    );
  }

  // ---- empty tab ----
  if (listClaims.length === 0 && !claimId) {
    const copy =
      view === 'underpaid'
        ? 'Nothing underpaid. Every partial payment matched the patient’s plan.'
        : 'No denied claims. When a payer denies one, ELVA prepares what to do here.';
    return (
      <div className="apl apl-page">
        {header}
        <div className="apl-empty">
          <p className="apl-empty-title">{view === 'underpaid' ? 'Nothing underpaid.' : 'No denied claims.'}</p>
          <p className="apl-meta">{copy}</p>
        </div>
        {renderDialogs()}
        {renderToasts()}
      </div>
    );
  }

  const mobileClass = !isDesktop ? (claimId ? 'apl-two-detail' : 'apl-two-queue') : '';

  let detail: React.ReactNode = null;
  if (claimId) {
    if (!selectedClaim) {
      detail = <NotFoundInPage as="main" thing="claim" id={claimId} listLabel="denied claims" listTo="/denied" />;
    } else if (selectedClaim.kind === 'underpaid') {
      detail = (
        <UnderpaidDetail
          key={selectedClaim.id}
          claim={selectedClaim}
          handlers={handlers}
          onPreview={doPreview}
          {...(!isDesktop ? { onBack: () => { void navigate('/denied/underpaid'); } } : {})}
        />
      );
    } else {
      detail = (
        <DeniedDetail
          key={selectedClaim.id}
          claim={selectedClaim}
          handlers={handlers}
          onPreview={doPreview}
          onDecide={doDecide}
          {...(!isDesktop ? { onBack: () => { void navigate('/denied'); } } : {})}
        />
      );
    }
  }

  return (
    <div className={`apl apl-page ${mobileClass}`}>
      {header}
      <div className="apl-body">
        <Queue
          view={view}
          claims={listClaims}
          {...(claimId ? { selectedId: claimId } : {})}
          onSelect={goTo}
          onSearchAllTime={(q) => { void navigate(`/denied?tab=resolved&q=${encodeURIComponent(q)}`); }}
          postingCount={view === 'underpaid' ? 9 : 0}
          resolvedCount={DENIED_RESOLVED.length}
          onResolved={() => { void navigate('/denied?tab=resolved'); }}
        />
        {detail}
      </div>
      {renderDialogs()}
      {renderToasts()}
    </div>
  );

  function renderDialogs() {
    if (!dialog) return null;
    const row = dialog.row;
    return (
      <DecideDialog
        row={row}
        onCancel={() => { setDialog(null); }}
        onConfirm={() => {
          decideSent(row.id);
          setDialog(null);
          addToast(`${row.patient}’s appeal is closed.`);
        }}
      />
    );
  }

  function renderToasts() {
    if (toasts.length === 0) return null;
    return (
      <div className="apl-toasts">
        {toasts.map((t) => (
          <div key={t.id} className="apl-toast" role="status">
            <span>{t.msg}</span>
            {t.undoLabel ? (
              <button
                type="button"
                className="apl-toast-undo"
                onClick={() => {
                  t.onUndo?.();
                  setToasts((list) => list.filter((x) => x.id !== t.id));
                }}
              >
                {t.undoLabel}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    );
  }
}
